import NavButton from '@atoms/NavButton/NavButton';
import ShadowContainerHorizontal from '@atoms/ShadowContainerHorizontal/ShadowContainerHorizontal';
import Tag from '@atoms/Tag/Tag';
import { IImage } from '@models/IImage';
import { ILink } from '@models/ILink';
import { IVideo } from '@models/IVideo';
import BreakpointEnum from '@models/enums/BreakpointEnum';
import BreakpointTypeEnum from '@models/enums/BreakpointTypeEnum';
import HorizontalDirectionEnum from '@models/enums/HorizontalDirectionEnum';
import TagTypesEnum from '@models/enums/TagTypesEnum';
import Carousel from '@molecules/Carousel/Carousel';
import duplicateArrayItems from '@utils/helpers/duplicateArrayItems';
import slugify from '@utils/helpers/slugify';
import useBreakpoint from '@utils/hooks/useBreakpoint';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { SwiperSlide } from 'swiper/react';
import TabbedFeatureSlide from './TabbedFeatureSlide';

interface TabFeature {
  readonly media: IImage | IVideo;
  readonly title?: string;
  readonly text?: string;
  readonly primaryCTA?: ILink;
}

interface Tab {
  readonly title?: string;
  readonly features?: TabFeature[];
}

interface Props {
  readonly tabs?: Tab[];
  readonly anchorId?: string;
}

const TabbedFeatureCarousel = ({ tabs = [], anchorId = '' }: Props) => {
  const [slideIndex, setSlideIndex] = useState(0);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const viewportBreakpoint = useBreakpoint(BreakpointTypeEnum.Screen);
  const screenBreakpoint = useBreakpoint(BreakpointTypeEnum.Screen);
  const isAboveLargeScreen =
    screenBreakpoint === BreakpointEnum.Large ||
    screenBreakpoint === BreakpointEnum.XLarge;
  const displayedTabs = useMemo(
    () =>
      tabs.filter(
        (tab) =>
          tab.title &&
          !!(tab.features ?? []).filter((feature) => feature.media).length
      ),
    [tabs]
  );

  const navButtonId = `tabbed-feature-carousel-${anchorId}`;
  const tabsTitles = displayedTabs.map((tab) => tab.title) as string[];
  const activeCarousel = useMemo(
    () =>
      (displayedTabs[activeTabIndex]?.features ?? []).filter(
        (feature) => feature.media
      ),
    [displayedTabs, activeTabIndex]
  );
  const shouldLoop =
    screenBreakpoint !== BreakpointEnum.Small && activeCarousel.length > 1;
  const duplicatedItems = duplicateArrayItems(activeCarousel, 4);
  const itemsToDisplay = shouldLoop ? duplicatedItems : activeCarousel;

  if (displayedTabs.length === 0) return null;

  return (
    <section
      className="component-padding dark bg-black-100 text-white"
      id={`a-${anchorId}`}
      data-testid="Tabbed-Feature-Carousel"
    >
      <div
        className={classNames('container', {
          'flex items-center justify-between': isAboveLargeScreen,
        })}
      >
        <ShadowContainerHorizontal
          hasTabbableNestedItems
          className={classNames({
            'w-full max-w-[80%]': isAboveLargeScreen,
          })}
        >
          <div className="flex w-full gap-4 sm:pr-10 md:gap-5">
            {tabsTitles.map((tabTitle, index) => {
              const isActive = index === activeTabIndex;

              return (
                <Tag
                  key={`tag-tfc-${tabTitle}`}
                  tagType={
                    isActive ? TagTypesEnum.Secondary : TagTypesEnum.Primary
                  }
                  text={tabTitle}
                  pointerStates={!isActive}
                  disabled={isActive}
                  onClick={() => {
                    setActiveTabIndex(index);
                    setSlideIndex(0);
                  }}
                  dataTestId={`Tabbed-Feature-Carousel-Tab-${index}`}
                />
              );
            })}
          </div>
        </ShadowContainerHorizontal>

        <div
          className={classNames(
            'gap-5',
            isAboveLargeScreen ? 'flex' : 'hidden'
          )}
        >
          <NavButton
            id={navButtonId}
            direction={HorizontalDirectionEnum.Left}
          />
          <NavButton
            id={navButtonId}
            direction={HorizontalDirectionEnum.Right}
          />
        </div>
      </div>

      <div className="overflow-swiper relative mt-10 w-full overflow-hidden xl:mt-10">
        <AnimatePresence>
          <ShadowContainerHorizontal
            showStart={
              viewportBreakpoint !== BreakpointEnum.XLarge &&
              (shouldLoop || slideIndex > 0)
            }
            showEnd={
              viewportBreakpoint !== BreakpointEnum.XLarge &&
              (shouldLoop || slideIndex < activeCarousel.length - 1)
            }
            disableScroll
            shadowColour="from-black-100"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{
                opacity: 0,
                transition: {
                  duration: 0,
                },
              }}
              key={tabsTitles[activeTabIndex]}
              className="container whitespace-normal"
            >
              <Carousel
                breakpoints={{
                  0: {
                    slidesPerView: activeCarousel.length === 1 ? 1 : 1.2,
                    spaceBetween: activeCarousel.length === 1 ? 0 : 20,
                  },
                  768: {
                    // Swiper v10 needs a value greater than 1 to properly show the prev/next slides when looping
                    slidesPerView: activeCarousel.length === 1 ? 1 : 1.001,
                    spaceBetween: activeCarousel.length === 1 ? 0 : 20,
                    centeredSlides: true,
                  },
                  1440: {
                    // Swiper v10 needs a value greater than 1 to properly show the prev/next slides when looping
                    slidesPerView: 1.001,
                    spaceBetween: 0,
                    centeredSlides: true,
                  },
                }}
                loop={shouldLoop}
                onSlideChange={(swiper) => setSlideIndex(swiper.activeIndex)}
                navId={navButtonId}
              >
                {itemsToDisplay.map((feature, index) => (
                  <SwiperSlide
                    key={slugify(
                      `tabbed-feature-slide${feature.title}-${feature.primaryCTA?.url}-${index}`
                    )}
                    className="tabbed-feature stretch-card"
                  >
                    <TabbedFeatureSlide
                      {...feature}
                      dataTestId={`Tabbed-Feature-Carousel-Slide-${index}`}
                    />
                  </SwiperSlide>
                ))}
              </Carousel>
            </motion.div>
          </ShadowContainerHorizontal>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default TabbedFeatureCarousel;
